import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { navigationSections, type NavSection } from "@/mocks/navigation";
import { useUser } from "@/context/UserContext";

interface SidebarProps {
  isCollapsed: boolean;
  userRole: string;
  accessiblePages?: string[];
  restrictedPages?: string[];
  hiddenPages?: string[];
  onToggle?: () => void;
  unreadManagerCount?: number;
  unreadDeptChatCount?: number;
}

function playDingDong() {
  try {
    const ctx = new AudioContext();
    const playTone = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.9, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };
    // Ding (higher) ~ 880Hz, 0.15s
    playTone(880, 0, 0.15);
    // Dong (lower) ~ 660Hz, 0.2s, slightly after
    playTone(660, 0.18, 0.2);
    setTimeout(() => ctx.close(), 600);
  } catch {
    // Audio not supported, silently ignore
  }
}

export default function Sidebar({ isCollapsed, userRole, accessiblePages = [], restrictedPages = [], hiddenPages = [], onToggle, unreadManagerCount = 0, unreadDeptChatCount = 0 }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const prevManagerRef = useRef(unreadManagerCount);
  const prevDeptChatRef = useRef(unreadDeptChatCount);
  const { isAdmin, isProfileComplete } = useUser();

  // Non-admin with incomplete profile: only profile page is clickable
  const navLocked = !isAdmin && !isProfileComplete;

  // Play ding-dong when manager unread count goes up
  useEffect(() => {
    if (unreadManagerCount > prevManagerRef.current) {
      playDingDong();
    }
    prevManagerRef.current = unreadManagerCount;
  }, [unreadManagerCount]);

  // Play ding-dong when dept chat unread count goes up
  useEffect(() => {
    if (unreadDeptChatCount > prevDeptChatRef.current) {
      playDingDong();
    }
    prevDeptChatRef.current = unreadDeptChatCount;
  }, [unreadDeptChatCount]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const visibleSections = navigationSections
    .map((section: NavSection) => ({
      ...section,
      items: section.items.filter((item) => {
        // Hidden pages should not appear in navigation at all
        if (hiddenPages.includes(item.id)) return false;

        // If accessiblePages is provided, use it as the source of truth
        if (accessiblePages.length > 0 || restrictedPages.length > 0) {
          return accessiblePages.includes(item.id) || restrictedPages.includes(item.id);
        }
        // Fallback to hardcoded roles
        return item.roles.includes(userRole);
      }),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="flex flex-col h-full">
      {/* Logo Area */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-background-200">
        {!isCollapsed && (
          <img
            src="/cargo-platform-logo.png"
            alt="貨物管理平台"
            className="h-14 min-w-0 flex-1 object-contain object-left"
          />
        )}
        {onToggle && !isCollapsed && (
          <button
            onClick={onToggle}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500 cursor-pointer shrink-0"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
        )}
        {onToggle && isCollapsed && (
          <button
            onClick={onToggle}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500 cursor-pointer mx-auto"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        )}
      </div>

      {/* Navigation Sections */}
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {visibleSections.map((section) => (
          <div key={section.title} className="mb-2">
            {/* Section Title */}
            {!isCollapsed && (
              <div className="px-3 py-1.5">
                <span className="text-[11px] font-medium text-foreground-400 uppercase tracking-wider">
                  {section.title}
                </span>
              </div>
            )}
            {/* Section Items */}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isManagerCenter = item.id === "manager-center";
                const isDeptChat = item.id === "department-chat";
                const isProfileItem = item.id === "profile";
                const showBadge = (isManagerCenter && unreadManagerCount > 0) || (isDeptChat && unreadDeptChatCount > 0);
                const badgeCount = isManagerCenter ? unreadManagerCount : isDeptChat ? unreadDeptChatCount : 0;
                const isLocked = navLocked && !isProfileItem;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (isLocked) return;
                      navigate(item.path);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${(isManagerCenter || isDeptChat) ? "relative" : ""} ${
                      isActive(item.path)
                        ? "bg-primary-50 text-primary-700"
                        : isLocked
                          ? "text-foreground-400"
                          : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      {isLocked ? (
                        <i className="ri-lock-line text-foreground-300" />
                      ) : (
                        <i
                          className={`${item.icon} ${
                            isActive(item.path)
                              ? "text-primary-500"
                              : "text-foreground-400"
                          }`}
                        />
                      )}
                    </div>
                    {!isCollapsed && (
                      <span className="whitespace-nowrap flex-1">
                        {item.label}
                      </span>
                    )}
                    {showBadge && !isCollapsed && (
                      <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-accent-500 text-background-50 text-[10px] flex items-center justify-center font-bold">
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                    {showBadge && isCollapsed && (
                      <span className="absolute top-0.5 right-0.5 min-w-[1rem] h-4 px-0.5 rounded-full bg-accent-500 text-background-50 text-[9px] flex items-center justify-center font-bold">
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}